import { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { RootState } from 'store/modules/rootReducer'
import {
  setCurrentPlan,
  setPlansList,
  setLoading,
  setError,
  removePlan as removePlanAction,
} from 'store/modules/studyPlan/actions'
import { IStudyPlan } from 'store/modules/studyPlan/types'
import { planningApi } from '../services/api'

interface UseStudyPlanManagerReturn {
  // Estado
  currentPlan: IStudyPlan | null
  plans: IStudyPlan[]
  loading: boolean
  error: string | null
  mode: 'view' | 'create' | 'edit'

  // Ações
  loadPlans: () => Promise<void>
  loadPlanById: (planId: string) => Promise<IStudyPlan | null>
  selectPlan: (planId: string) => void
  deletePlan: (planId: string) => Promise<void>
  refreshCurrentPlan: () => Promise<void>
}

export const useStudyPlanManager = (): UseStudyPlanManagerReturn => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { id } = useParams<{ id?: string }>()

  const { currentPlanId, plans, loading, error } = useSelector(
    (state: RootState) => state.studyPlan
  )

  const [currentPlan, setCurrentPlanState] = useState<IStudyPlan | null>(null)

  // Determina o modo baseado na URL
  const mode: 'view' | 'create' | 'edit' = (() => {
    if (location.pathname === '/planejamento/novo') return 'create'
    if (id) return 'edit'
    return 'view'
  })()

  /**
   * Carrega a lista de planos do usuário
   */
  const loadPlans = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      const response = await planningApi.getSavedSchedules()
      dispatch(setPlansList(response))
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao carregar planos de estudo'
      dispatch(setError(errorMessage))
      console.error('Error loading plans:', err)
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch])

  /**
   * Carrega um plano específico por ID
   */
  const loadPlanById = useCallback(
    async (planId: string): Promise<IStudyPlan | null> => {
      try {
        dispatch(setLoading(true))
        const plan = await planningApi.getScheduleById(planId)
        return plan
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Erro ao carregar plano de estudo'
        dispatch(setError(errorMessage))
        console.error('Error loading plan:', err)
        return null
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch]
  )

  /**
   * Seleciona um plano como atual
   */
  const selectPlan = useCallback(
    (planId: string) => {
      dispatch(setCurrentPlan(planId))
      const plan = plans.find((p) => p._id === planId)
      if (plan) {
        setCurrentPlanState(plan)
      }
    },
    [dispatch, plans]
  )

  /**
   * Deleta um plano
   */
  const deletePlan = useCallback(
    async (planId: string) => {
      try {
        dispatch(setLoading(true))
        await planningApi.deleteSchedule(planId)
        dispatch(removePlanAction(planId))

        // Se deletou o plano atual, redireciona
        if (planId === currentPlanId) {
          // Recarrega a lista para pegar o próximo plano
          await loadPlans()
          history.push('/planejamento')
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Erro ao deletar plano de estudo'
        dispatch(setError(errorMessage))
        console.error('Error deleting plan:', err)
        throw err
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, currentPlanId, history, loadPlans]
  )

  /**
   * Recarrega o plano atual
   */
  const refreshCurrentPlan = useCallback(async () => {
    if (currentPlanId) {
      const plan = await loadPlanById(currentPlanId)
      if (plan) {
        setCurrentPlanState(plan)
      }
    }
  }, [currentPlanId, loadPlanById])

  /**
   * Carrega os planos ao montar o componente
   */
  useEffect(() => {
    loadPlans()
  }, [loadPlans])

  /**
   * Gerencia o carregamento do plano baseado no modo
   */
  useEffect(() => {
    const loadCurrentPlan = async () => {
      if (mode === 'create') {
        // Modo criação - limpa o plano atual
        setCurrentPlanState(null)
      } else if (mode === 'edit' && id) {
        // Modo edição - carrega o plano específico da URL
        const plan = await loadPlanById(id)
        if (plan) {
          setCurrentPlanState(plan)
          dispatch(setCurrentPlan(id))
        }
      } else if (mode === 'view' && currentPlanId) {
        // Modo visualização - carrega o plano atual selecionado
        const plan = plans.find((p) => p._id === currentPlanId)
        if (plan) {
          setCurrentPlanState(plan)
        } else {
          // Se não encontrou nos planos carregados, busca da API
          const loadedPlan = await loadPlanById(currentPlanId)
          if (loadedPlan) {
            setCurrentPlanState(loadedPlan)
          }
        }
      }
    }

    loadCurrentPlan()
  }, [mode, id, currentPlanId, plans, loadPlanById, dispatch])

  return {
    currentPlan,
    plans,
    loading,
    error,
    mode,
    loadPlans,
    loadPlanById,
    selectPlan,
    deletePlan,
    refreshCurrentPlan,
  }
}
